import { useEffect, useRef, type KeyboardEvent } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import editorCopy from '@/i18n/ns/workspace-editors';
import { useWorkspace } from '../context';
import { useWorkspaceText } from '../interface-copy';
import { useIntlLocale } from '../intl-locale';
import { WorkspaceIcon } from '../icons';
import { LoadingState } from '../primitives';
import { startProjectChat } from './home';
import { takeAboutOpener, takeAboutPushed } from './about-state';
import { ProjectFaces, ProjectMark } from './mark';
import { projectPlace } from './place';
import { endsLabel, firstName, isViewer, peopleIn, shortWhen } from './labels';
import { useKnownProject, useProject, useProjectMissing } from './use-projects';
import type { ProjectDetail, ProjectPerson } from './types';

// About this project: the brief, the Skills every chat here follows, what it reads first and
// the people, beside the work, the way a chat keeps its details out of the conversation.

type ProjectCopy = typeof workspaceFeaturesCopy.en;

/**
 * A sentence about people that may include you: you have sentences of your own, since "you"
 * changes the verb in most of the 24 languages ("Vous y travaillez", not "Vous y travaillent").
 */
function aboutPeople(
  people: ProjectPerson[],
  viewerId: string | undefined,
  locale: string,
  copy: ProjectCopy,
  sentences: {
    one: keyof ProjectCopy;
    many: keyof ProjectCopy;
    you: keyof ProjectCopy;
    youAnd: keyof ProjectCopy;
  },
) {
  const rest = people.filter((person) => !isViewer(person, viewerId));
  const key =
    rest.length === people.length
      ? rest.length === 1
        ? sentences.one
        : sentences.many
      : rest.length
        ? sentences.youAnd
        : sentences.you;
  return formatCopy(copy[key], { people: peopleIn(copy[key], 'people', rest, locale) });
}

// Below this width the panel covers the home (projects.css), so keyboard focus stays in it.
const COVERS_HOME = '(max-width: 1100px)';
const shown = (element: Element | null): element is HTMLElement =>
  element instanceof HTMLElement && element.isConnected && element.getClientRects().length > 0;

/**
 * About this project, beside the project's home and below the top bar, as the design has it. Its
 * place is the route (`canvas=project`), so Back and a link open and close it like any panel.
 */
export function ProjectAboutPanel({ id }: { id: string }) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const { text: uiText } = useWorkspaceText();
  const missing = useProjectMissing(id);
  const remembered = useKnownProject(id) ?? projectPlace(id);
  const known = missing ? null : remembered;
  const heading = useRef<HTMLHeadingElement>(null);
  const panel = useRef<HTMLElement>(null);
  // Opened by its link, closing steps Back to the home; otherwise it closes in place.
  const close = () => {
    if (takeAboutPushed()) history.back();
    else ws.openCanvas(null, { replace: true });
  };
  useEffect(() => {
    heading.current?.focus();
    const opener = takeAboutOpener();
    return () => {
      takeAboutPushed();
      // The About button that opened it comes back as the panel closes: focus returns there,
      // or to the one showing (a phone has only the one above the composer), unless the person
      // has already moved on to something else.
      requestAnimationFrame(() => {
        if (document.activeElement && document.activeElement !== document.body) return;
        const buttons = [...document.querySelectorAll('[data-project-about]')].filter(shown);
        const back =
          buttons.find((button) => button.getAttribute('data-project-about') === opener) ??
          buttons[0];
        back?.focus();
      });
    };
  }, []);
  const keepFocusInside = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab' || !panel.current || !window.matchMedia(COVERS_HOME).matches) return;
    const stops = [
      ...panel.current.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter(shown);
    if (!stops.length) return;
    const first = stops[0];
    const last = stops[stops.length - 1];
    const at = document.activeElement;
    if (event.shiftKey && (at === first || at === heading.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && at === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return (
    <aside
      ref={panel}
      className="ws-project-about-panel"
      aria-labelledby="project-about-title"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !event.defaultPrevented) close();
        else keepFocusInside(event);
      }}
    >
      <header>
        {known && <ProjectMark name={known.name} tint={known.tint} size="sm" />}
        <h2 id="project-about-title" ref={heading} tabIndex={-1}>
          {copy.aboutProject}
        </h2>
        <button
          type="button"
          className="ws-icon-button"
          aria-label={uiText('Close')}
          onClick={close}
        >
          <WorkspaceIcon name="close" size={16} />
        </button>
      </header>
      <div className="ws-project-about-scroll">
        <ProjectAbout id={id} />
      </div>
    </aside>
  );
}

/** Who can't open a reads-first item: one person or several, never you (it lists only what you
 * can open). */
function cannotOpenLine(people: ProjectPerson[], locale: string, copy: ProjectCopy) {
  const template =
    people.length === 1 ? copy.personCannotOpenWithout : copy.peopleCannotOpenWithout;
  return formatCopy(template, { people: peopleIn(template, 'people', people, locale) });
}

function briefVersion(
  project: ProjectDetail,
  viewerId: string | undefined,
  locale: string,
  copy: ProjectCopy,
) {
  const version = formatCopy(copy.version, { count: project.briefRevision });
  const by = project.briefChangedBy;
  if (!by || !project.briefChangedAt) return version;
  const written = project.briefRevision === 1;
  const key = isViewer(by, viewerId)
    ? written
      ? 'briefWrittenByYou'
      : 'briefChangedByYou'
    : !by.userId
      ? written
        ? 'briefWrittenByFormer'
        : 'briefChangedByFormer'
      : written
        ? 'briefWrittenBy'
        : 'briefChangedBy';
  return formatCopy(copy[key], {
    version,
    name: firstName(by.name),
    when: shortWhen(project.briefChangedAt, locale, new Date(), true),
  });
}

function peopleLine(
  project: ProjectDetail,
  viewerId: string | undefined,
  companyName: string,
  locale: string,
  copy: ProjectCopy,
) {
  const others = project.people.filter((person) => person.userId !== project.owner.userId);
  const lead = isViewer(project.owner, viewerId)
    ? copy.youLeadIt
    : project.owner.userId
      ? formatCopy(copy.personLeadsIt, { name: firstName(project.owner.name) })
      : copy.formerColleagueLeadsIt;
  if (others.length)
    return `${lead} ${aboutPeople(others, viewerId, locale, copy, {
      one: 'personWorksInIt',
      many: 'peopleWorkInIt',
      you: 'youWorkInIt',
      youAnd: 'youAndPeopleWorkInIt',
    })}`;
  if (project.visibility === 'company')
    return `${lead} ${formatCopy(copy.everyoneAtCanFind, { company: companyName })}`;
  return `${lead} ${copy.shareWhenOthers}`;
}

function ProjectAbout({ id }: { id: string }) {
  const ws = useWorkspace();
  const locale = useIntlLocale();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editorText = useNamespaceCopy(editorCopy);
  const { project, loading, missing } = useProject(id, { revalidate: true });
  if (missing)
    return (
      <div className="ws-project-about">
        <p className="ws-muted">{copy.aboutUnavailable}</p>
      </div>
    );
  if (!project) return loading ? <LoadingState label={copy.openingProject} /> : null;
  const editor = project.role !== 'viewer';
  return (
    <div className="ws-project-about" data-testid="project-about">
      <section>
        <h3>
          {copy.brief} <small>{briefVersion(project, ws.user?.id, locale, copy)}</small>
        </h3>
        {project.brief.trim() ? (
          <p>{project.brief}</p>
        ) : (
          <p className="ws-muted">{editor ? copy.noBriefLong : copy.noBrief}</p>
        )}
        {editor && (
          <button
            type="button"
            className="ws-text-button ws-project-about-change"
            onClick={() =>
              startProjectChat(ws, project, `${editorText.changeProjectBriefPrompt} `, false)
            }
          >
            {copy.changeInChat} <WorkspaceIcon name="arrow" size={14} />
          </button>
        )}
      </section>
      {project.follows.length > 0 && (
        <section>
          <h3>{copy.everyChatFollows}</h3>
          <ul>
            {project.follows.map((skill) => (
              <li key={skill.id}>
                <button
                  type="button"
                  onClick={() => ws.openCanvas({ kind: 'skill', id: skill.id })}
                >
                  <WorkspaceIcon name="skills" size={15} />
                  <span>{skill.name}</span>
                  <small>{copy.skill}</small>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {project.readsFirst.length > 0 && (
        <section>
          <h3>{copy.andReadsFirst}</h3>
          <ul>
            {project.readsFirst.map((item) => (
              <li key={`${item.kind}:${item.id}`}>
                <button
                  type="button"
                  onClick={() =>
                    ws.openCanvas(
                      item.kind === 'file'
                        ? { kind: 'file', id: item.id }
                        : { kind: 'page', id: item.id },
                    )
                  }
                >
                  <WorkspaceIcon name={item.kind === 'file' ? 'file' : 'knowledge'} size={15} />
                  <span>{item.title}</span>
                  <small>{item.kind === 'file' ? copy.file : copy.knowledge}</small>
                </button>
                {editor && item.cannotOpen && item.cannotOpen.length > 0 && (
                  <p className="ws-project-about-warn" role="status">
                    {cannotOpenLine(item.cannotOpen, locale, copy)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <section>
        <h3>
          {copy.people}
          {project.endsOn && <small>{endsLabel(project.endsOn, project.finished, locale)}</small>}
        </h3>
        <div className="ws-project-about-people">
          <ProjectFaces people={project.people} max={3} />
          <span>{peopleLine(project, ws.user?.id, ws.companyName, locale, copy)}</span>
        </div>
      </section>
      <section>
        <p className="ws-project-about-note">
          <WorkspaceIcon name="shield" size={14} />
          {copy.projectAccessNote}
        </p>
      </section>
    </div>
  );
}
